import React, { useState, useEffect, useRef } from "react";
import "./BatchStatus.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ActionBar from "../Components/BottomActionBar/ActionBar";
import { batchStatusAPI, batchDetailsAPI, emailAPI } from "./api";
import DataTable from "./DataTable";

function BatchStatus() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [batchSrNo, setBatchSrNo] = useState("");
  const [batchSize, setBatchSize] = useState("");
  const [numDays, setNumDays] = useState("");
  const [batchStatus, setBatchStatus] = useState("");

  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [viewMsg, setViewMsg] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [lastKey, setLastKey] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const lastKeyRef = useRef(null);
  const [cancelChecked, setCancelChecked] = useState({});
  const [cancelReasons, setCancelReasons] = useState({});
  const [cancelling, setCancelling] = useState(false);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  useEffect(() => {
    if (startDate && endDate) {
      const diffDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      setNumDays(diffDays > 0 ? diffDays : "Invalid range");
    } else {
      setNumDays("");
    }
  }, [startDate, endDate]);

  const clearMessages = () => setViewMsg(null);

  const buildParams = () => {
    const params = { limit: 25 };
    if (batchSrNo.trim()) params.batch_sr_no = batchSrNo.trim();
    if (batchSize.trim()) params.batch_size = batchSize.trim();
    if (batchStatus) params.trng_status = batchStatus.trim();
    if (startDate) params.start_date = startDate.toLocaleDateString('en-CA');
    if (endDate) params.end_date = endDate.toLocaleDateString('en-CA');
    return params;
  };

  const handleView = async () => {
    clearMessages();
    try {
      const res = await batchStatusAPI.getPage(buildParams(), null);
      setTableData(Array.isArray(res.items) ? res.items : []);
      lastKeyRef.current = res.lastKey ?? null;
      setLastKey(res.lastKey ?? null);
      setHasMore(res.hasMore ?? !!res.lastKey);
      setShowTable(true);
    } catch {
      setTableData([]);
      lastKeyRef.current = null;
      setLastKey(null);
      setHasMore(false);
      setShowTable(true);
    }
  };

  const handleLoadMore = async () => {
    if (!lastKeyRef.current || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await batchStatusAPI.getPage(buildParams(), lastKeyRef.current);
      const newItems = Array.isArray(res.items) ? res.items : [];
      setTableData((prev) => [...prev, ...newItems]);
      lastKeyRef.current = res.lastKey ?? null;
      setLastKey(res.lastKey ?? null);
      setHasMore(res.hasMore ?? !!res.lastKey);
    } catch (e) {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setBatchSrNo("");
    setBatchSize("");
    setNumDays("");
    setBatchStatus("");
    setTableData([]);
    setShowTable(false);
    setViewMsg(null);
    setSortBy("");
    setLastKey(null);
    setHasMore(false);
    lastKeyRef.current = null;
    setCancelChecked({});
    setCancelReasons({});
  };

  const handleCancelBatches = async () => {
    const toCancel = tableData.filter((row) => (row.trng_status === 'FORMED' || row.trng_status === 'SCHEDULED') && cancelChecked[row.batch_sr_no]);
    const missing = toCancel.filter((row) => !cancelReasons[row.batch_sr_no]?.trim());
    if (missing.length) {
      alert('Please enter a reason for all selected batches before cancelling.');
      return;
    }
    setCancelling(true);
    try {
      await Promise.all(
        toCancel.map((row) =>
          batchStatusAPI.update(row.batch_sr_no, { trng_status: 'CANCELLED', reason: cancelReasons[row.batch_sr_no].trim() })
        )
      );

      // Send cancellation emails for each cancelled batch
      for (const row of toCancel) {
        const trainingData = {
          batchSrNo: row.batch_sr_no,
          trngCourseName: row.trng_course_name ?? '',
          funcSubAreaName: row.func_sub_area_name ?? '',
          startDate: row.start_date ? row.start_date.split('T')[0] : '',
          endDate: row.end_date ? row.end_date.split('T')[0] : '',
          startTime: row.start_time ?? '',
          endTime: row.end_time ?? '',
          venue: row.venue ?? '',
          batchSize: row.batch_size ?? '',
          faculty: row.faculty ?? '',
        };

        let participants = [];
        try { participants = await batchDetailsAPI.getParticipants(row.batch_sr_no); } catch { /* skip if no participants */ }

        // Send to each employee (L)
        const empEmailPromises = participants
          .filter((p) => p.emp_email)
          .map((p) =>
            emailAPI.cancellationEmployee({
              employeeData: { emailId: p.emp_email.replace('@godrej.com', ''), name: p.emp_name ?? '' },
              trainingData,
            }).catch(() => { })
          );

        // Send to each unique superior (L+1)
        const sentSups = new Set();
        const supEmailPromises = participants
          .filter((p) => p.sup_email && !sentSups.has(p.sup_emp_code) && sentSups.add(p.sup_emp_code))
          .map((p) => {
            const empNamesForSup = participants
              .filter((e) => e.sup_emp_code === p.sup_emp_code && e.emp_name)
              .map((e) => e.emp_name)
              .join(', ');
            return emailAPI.cancellationSuperior({
              superiorData: { emailId: p.sup_email.replace('@godrej.com', ''), name: p.sup_name ?? '' },
              employeeData: { name: empNamesForSup || '' },
              trainingData,
            }).catch(() => { });
          });

        await Promise.all([...empEmailPromises, ...supEmailPromises]);
      }

      alert(`${toCancel.length} batch(es) cancelled successfully! Cancellation emails sent.`);
      setCancelChecked({});
      setCancelReasons({});
      await handleView();
    } catch (err) {
      alert('Failed to cancel batches. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const sortTableData = (data, sortOption) => {
    if (!sortOption) return data;
    const sorted = [...data];
    switch (sortOption) {
      case "Batch Sr.No.":
        return sorted.sort((a, b) => String(a.batch_sr_no || "").localeCompare(String(b.batch_sr_no || "")));
      case "Start Date":
        return sorted.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      case "Status":
        return sorted.sort((a, b) => String(a.trng_status || "").localeCompare(String(b.trng_status || "")));
      default:
        return sorted;
    }
  };

  const handleSortChange = (sortOption) => {
    setSortBy(sortOption);
    if (tableData.length > 0) setTableData(sortTableData(tableData, sortOption));
  };

  return (
    <div className="Body" onClick={clearMessages}>
      <form onSubmit={(e) => e.preventDefault()}>
        <table className="formTable">
          <tbody>
            <tr>
              <td className="tdLabel">Batch Sr.No.</td>
              <td className="tdField">
                <input
                  className="bsInput"
                  type="text"
                  value={batchSrNo}
                  onChange={(e) => setBatchSrNo(e.target.value)}
                  onFocus={clearMessages}
                />
              </td>
              <td className="tdLabel">Batch Size</td>
              <td className="tdField">
                <input
                  className="bsInput"
                  type="text"
                  value={batchSize}
                  onChange={(e) => setBatchSize(e.target.value)}
                  onFocus={clearMessages}
                />
              </td>
            </tr>

            <tr>
              <td className="tdLabel">Start Date (DD/MM/YY)</td>
              <td className="tdField">
                <div className="bsDateWrap" onClick={() => setStartDateOpen(true)}>
                  <DatePicker
                    open={startDateOpen}
                    onClickOutside={() => setStartDateOpen(false)}
                    selected={startDate}
                    onChange={(date) => { setStartDate(date); setStartDateOpen(false); }}
                    dateFormat="dd/MM/yyyy"
                    className="bsDateInput"
                    placeholderText="Select start date"
                    showIcon
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={<input readOnly />}
                    onFocus={clearMessages}
                  />
                </div>
              </td>
              <td className="tdLabel">End Date (DD/MM/YY)</td>
              <td className="tdField">
                <div className="bsDateWrap" onClick={() => setEndDateOpen(true)}>
                  <DatePicker
                    open={endDateOpen}
                    onClickOutside={() => setEndDateOpen(false)}
                    selected={endDate}
                    onChange={(date) => { setEndDate(date); setEndDateOpen(false); }}
                    dateFormat="dd/MM/yyyy"
                    className="bsDateInput"
                    placeholderText="Select end date"
                    minDate={startDate}
                    showIcon
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={<input readOnly />}
                    onFocus={clearMessages}
                  />
                </div>
              </td>
            </tr>

            <tr>
              <td className="tdLabel">No. of Days</td>
              <td className="tdField">
                <input
                  className="bsInput"
                  type="text"
                  value={numDays}
                  readOnly
                />
              </td>
              <td className="tdLabel">Batch Status</td>
              <td className="tdField">
                <select
                  className="bsSelect"
                  value={batchStatus}
                  onChange={(e) => setBatchStatus(e.target.value)}
                  onFocus={clearMessages}
                >
                  <option value="">--Select--</option>
                  <option>FORMED</option>
                  <option>SCHEDULED</option>
                  <option>COMPLETED</option>
                  <option>CANCELLED</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      <ActionBar
        hideInsertUpdate
        sortOptions={["Batch Sr.No.", "Start Date", "Status"]}
        onView={handleView}
        onReset={handleReset}
        onSortChange={handleSortChange}
        selectedSort={sortBy}
      />

      {viewMsg && (
        <p style={{ textAlign: "center", fontSize: "11px", fontFamily: '"GEG-Body"', color: viewMsg.success ? "green" : "red", margin: "4px 0" }}>
          {viewMsg.text}
        </p>
      )}

      {showTable && (
        <>
          <DataTable
            columns={[
              { header: "Batch Sr.No.", accessor: (row) => row.batch_sr_no ?? "" },
              { header: "Start Date", accessor: (row) => row.start_date.split("T")[0] ?? "" },
              {
                header: "No. of Days", accessor: (row) => {
                  const sd = new Date(row.start_date);
                  const ed = new Date(row.end_date);
                  return Math.floor((ed.getTime() - sd.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                }
              },
              { header: "End Date", accessor: (row) => row.end_date.split("T")[0] ?? "" },
              { header: "Venue", accessor: (row) => row.venue ?? "" },
              { header: "Batch Size", accessor: (row) => row.batch_size ?? "" },
              { header: "Status", accessor: (row) => row.trng_status ?? "" },
              { header: "Division", accessor: (row) => row.division ?? "" },
              {
                header: "Reason",
                className: 'bs-reason-col',
                renderCell: (_v, row) => {
                  if (row.trng_status === 'FORMED' || row.trng_status === 'SCHEDULED') {
                    return (
                      <input
                        type="text"
                        value={cancelReasons[row.batch_sr_no] ?? ''}
                        onChange={(e) => setCancelReasons((prev) => ({ ...prev, [row.batch_sr_no]: e.target.value }))}
                        style={{ width: '100%', fontSize: 11, fontFamily: 'GEG-Body', padding: '3px 6px', boxSizing: 'border-box', border: '1px solid #bbb', borderRadius: 2 }}
                        placeholder="Enter reason"
                      />
                    );
                  }
                  if (row.trng_status === 'CANCELLED') {
                    return <span style={{ padding: '0 4px' }}>{row.reason ?? ''}</span>;
                  }
                  return null;
                },
              },
              {
                header: "Cancel",
                className: 'att-center',
                renderCell: (_v, row) =>
                  (row.trng_status === 'FORMED' || row.trng_status === 'SCHEDULED') ? (
                    <input
                      type="checkbox"
                      checked={cancelChecked[row.batch_sr_no] || false}
                      onChange={(e) => setCancelChecked((prev) => ({ ...prev, [row.batch_sr_no]: e.target.checked }))}
                      style={{ margin: '0 auto', display: 'block' }}
                    />
                  ) : null,
              },
            ]}
            data={tableData}
            showEditCols={false}
            editRows={{}}
            onToggleEditRow={() => { }}
            onChangeEditCell={() => { }}
            selectedForDelete={{}}
            onToggleDeleteRow={() => { }}
            emptyMessage="No data found"
            rowKey={(row, idx) => `${row.batch_sr_no}-${idx}`}
          />

          <div style={{ display: "flex", justifyContent: "center", margin: "12px 0", fontSize: 11, fontFamily: "GEG-Body" }}>
            {hasMore && (
              <button
                className="btn btn-outline"
                onClick={handleLoadMore}
                disabled={loadingMore}
                style={loadingMore ? { cursor: "not-allowed", opacity: 0.6 } : {}}
              >
                {loadingMore ? "Loading..." : "Load More"}
              </button>
            )}
            {!hasMore && tableData.length > 0 && (
              <span style={{ color: "#888" }}>All {tableData.length} record(s) loaded.</span>
            )}
          </div>
          {Object.values(cancelChecked).some(Boolean) && (
            <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
              <button
                className="btn btn-outline"
                onClick={handleCancelBatches}
                disabled={cancelling}
                style={cancelling ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
              >
                {cancelling ? 'Cancelling...' : 'Cancel Batches'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default BatchStatus;
